import React, { useState, useRef } from "react";
import styled from "styled-components";
import { useTranslation } from "next-i18next";
import { ProductCategoryCard } from "./ProductCategoryCard";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
  width: 100%;
  max-width: 1388px;
  margin: 0 auto;

  @media (max-width: 440px) {
    gap: 0px;
  }
`;

const Headline = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 5px;
  width: 100%;
  max-width: 100%;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  display: flex;
  align-items: center;
  color: #202123;
  margin: 0;

  @media (max-width: 440px) {
    font-size: 20px;
    line-height: 28px;
  }
`;

const Subtitle = styled.p`
  font-style: normal;
  font-size: 15px;
  line-height: 24px;
  display: flex;
  align-items: center;
  color: #5f6468;
  margin: 0;
`;

const ListContainer = styled.div<{ $cols: number }>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  padding: 16px 0;
  margin: 0;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;

  /* Hide scrollbars */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  > * {
    flex: 1 0 163px;
    max-width: 163px;
  }

  @media (max-width: 440px) {
    display: grid;
    grid-template-columns: repeat(${(props) => props.$cols}, 100px);
    grid-template-rows: repeat(2, 180px);
    grid-auto-flow: row;
    gap: 8px;
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    padding: 8px 0;

    > * {
      flex: initial;
      max-width: initial;
      scroll-snap-align: start;
    }
  }
`;

const MobileIndicatorWrapper = styled.div`
  display: none;

  @media (max-width: 440px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 4px;
    width: 343px;
    max-width: 100%;
    margin-top: 8px;
    align-self: center;
  }
`;

const IndicatorDot = styled.button<{ $active: boolean }>`
  width: ${(props) => (props.$active ? "16px" : "4px")};
  height: 4px;
  border-radius: 5px;
  background-color: ${(props) => (props.$active ? "#435DFF" : "#9FA6AD")};
  border: none;
  padding: 0;
  cursor: pointer;
  transition: width 0.3s ease, background-color 0.3s ease;
  flex: none;
  outline: none;
`;

export interface ProductCategoryCardListProps {
  children?: React.ReactNode;
  selectedCard?: string | null;
  onCardClick?: (categoryId: string) => void;
  onSelectCategory?: (categoryId: string) => void;
}

export const ProductCategoryCardList = ({
  children = undefined,
  selectedCard = undefined,
  onCardClick = undefined,
  onSelectCategory = undefined,
}: ProductCategoryCardListProps) => {
  const { t } = useTranslation("icth-retail-main");
  const [activeIndex, setActiveIndex] = useState(0);
  const slideContainerRef = useRef<HTMLDivElement>(null);
  const isManualScroll = useRef(false);

  const handleCardClick = (categoryId: string) => {
    // if (onCardClick) onCardClick(categoryId);
    // if (onSelectCategory) onSelectCategory(categoryId);
    window.location.pathname = `${window.location.pathname}/retail`;
  };

  const categoriesList = t("productCategory.list", {
    returnObjects: true,
  }) as Array<{
    id: string;
    title: string;
    englishTitle?: string;
    badgeText?: string;
    icon?: string;
    image?: string;
  }>;

  const cards = children
    ? React.Children.toArray(children)
    : Array.isArray(categoriesList) &&
      categoriesList.map((category) => {
        const isSelected = selectedCard === category.id;

        return (
          <ProductCategoryCard
            key={category.id}
            title={category.title}
            badgeText={category.badgeText}
            icon={category.icon}
            image={category.image}
            selected={isSelected}
            onClick={() => handleCardClick(category.id)}
          />
        );
      });

  const totalCards = Array.isArray(cards) ? cards.length : 0;
  const slideCount = Math.ceil(totalCards / 4);

  const pages = [];
  if (Array.isArray(cards)) {
    for (let i = 0; i < totalCards; i += 4) {
      const cardItem = cards[i];
      const keyStr =
        React.isValidElement(cardItem) && cardItem.key
          ? String(cardItem.key)
          : `fallback-page-${i}`;
      pages.push({
        id: `card-page-${keyStr}`,
        index: i / 4,
      });
    }
  }

  const handleScroll = () => {
    if (!slideContainerRef.current || isManualScroll.current) return;
    const container = slideContainerRef.current;
    const scrollPosition = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    if (maxScroll > 0 && scrollPosition >= maxScroll - 10) {
      setActiveIndex(slideCount - 1);
      return;
    }

    const pageStep = (100 + 8) * 2;
    const threshold = pageStep * 0.35;
    const newIndex = Math.floor((scrollPosition + threshold) / pageStep);
    if (newIndex >= 0 && newIndex < slideCount && newIndex !== activeIndex) {
      setActiveIndex(newIndex);
    }
  };

  const handlePageIndicatorChange = (index: number) => {
    setActiveIndex(index);
    if (slideContainerRef.current) {
      isManualScroll.current = true;
      const container = slideContainerRef.current;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const pageStep = (100 + 8) * 2;
      let targetScroll = index * pageStep;
      if (targetScroll > maxScroll) {
        targetScroll = maxScroll;
      }
      container.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
      setTimeout(() => {
        isManualScroll.current = false;
      }, 500);
    }
  };

  return (
    <Wrapper>
      <Headline>
        <Title>{t("productCategory.title")}</Title>
        <Subtitle>{t("productCategory.subtitle")}</Subtitle>
      </Headline>
      <ListContainer
        ref={slideContainerRef}
        onScroll={handleScroll}
        $cols={Math.ceil(totalCards / 2)}
      >
        {cards}
      </ListContainer>
      {slideCount > 1 && (
        <MobileIndicatorWrapper>
          {pages.map((page) => (
            <IndicatorDot
              key={page.id}
              $active={page.index === activeIndex}
              onClick={() => handlePageIndicatorChange(page.index)}
              aria-label={`Go to slide ${page.index + 1}`}
            />
          ))}
        </MobileIndicatorWrapper>
      )}
    </Wrapper>
  );
};

export default ProductCategoryCardList;
